import Joi from "joi";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import { ClientNavBar } from "../../components/Clients/ClientNavBar";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import SearchSelectComponent from "../../components/SearchSelectComponent";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Toast from "../../helpers/Toast";
import { ClientDTO } from "../../models/Client/ClientDTO";
import { ClientUpdateDTO } from "../../models/Client/ClientUpdateDTO";
import { AssistanceService } from "../../services/AssistanceService";
import { ClientService } from "../../services/ClientService";
import { CountryService } from "../../services/CountryService";
import useValidationSchema, { FieldConfig } from "../../components/Validations";

export type SelectItemsProps = {
  label: string;
  value: string;
};
export default function ClientEdit() {
  const { id } = useParams<{ id: string }>();
  const intl = useIntl();
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);

  const [client, setClient] = useState<ClientDTO>({} as ClientDTO);

  const [countrySelect, setCountrySelect] = useState<SelectItemsProps | null>(null);
  const clientService = new ClientService();
  const countryService = new CountryService();
  const navigate = useNavigate();
  const { availableModules } = useAvailableModulesContext();

  //RENTABILIDADE
  const [isActiveProfitabilityModule, setIsActiveProfitabilityModule] = useState<boolean>(false);
  const assistanceService = new AssistanceService();

  // Validações (campo, tipo dados, mensagem (pt.json))
  const fields: FieldConfig[] = [
    ["name", "string", "Screens.Client.Edit.Validation.NameRequired"],
    ["phoneNumber", "number", "Screens.Client.Edit.Validation.PhoneNumberRequired"],
    ["email", "string", "Screens.Client.Edit.Validation.EmailRequired"],
    ["countryId", "number", "Screens.Client.Edit.Validation.CountryRequired"]
  ];



  const schemaClientValidation = useValidationSchema(fields);
  const getCountry = async (countryId: number) => {
    var responseCountry = await countryService.GetCountryById(countryId)
    if (responseCountry.success === false || responseCountry.obj == null) {
      return;
    }

    setCountrySelect({
      label: responseCountry.obj.name,
      value: responseCountry.obj.id.toString()
    })
  }
  const searchCountry = async (nameCountry: string): Promise<SelectItemsProps[]> => {
    const response = await countryService.SearchCountryByName(nameCountry);
    if (response.success === false || response.obj === null) {
      Toast.Show("error", response.message)
    }

    const list: SelectItemsProps[] = [];

    response.obj?.forEach((element) => {
      const item: SelectItemsProps = {
        value: element.id.toString(),
        label: element.name
      }
      list.push(item)
    });
    return list;
  };

  const getClient = async () => {
    var response = await clientService.GetByIdErp(id);
    if (response.success === false || response.obj == null) {
      Toast.Show("error", response.message);
      return;
    }

    setClient({
      id: response.obj.id,
      erpId: response.obj.erpId,
      vatNumber: response.obj.vatNumber,
      name: response.obj.name,
      billingCode: response.obj.billingCode,
      billingCodeDebtTime: response.obj.billingCodeDebtTime,
      phoneNumber: response.obj.phoneNumber,
      email: response.obj.email,
      paymentConditions: response.obj.paymentConditions,
      website: response.obj.website,
      nameCountry: String(getCountry(response.obj.countryId)),
      address: response.obj.address,
      address2: response.obj.address2,
      locality: response.obj.locality,
      postalCode: response.obj.postalCode,
      countryId: response.obj.countryId
    })

    setLoadingUpdate(false);
  };

  useEffect(() => {
    getClient();
  }, []);


  const editClient = async () => {
    setLoadingUpdate(true)

    const updatedClient: ClientUpdateDTO = {
      erpId: client.erpId || id,
      id: client.id,
      name: client.name,
      vatNumber: client.vatNumber,
      phoneNumber: client.phoneNumber,
      email: client.email,
      street: client.address,
      street2: client.address2,
      postalCode: client.postalCode,
      city: client.locality,
      countryId: Number(countrySelect?.value),
      paymentType: client.paymentConditions,
      website: client.website,
    };

    const { error } = schemaClientValidation.validate(updatedClient);
    if (error) {
      Toast.Show("error", error.details.map(detail => detail.message).join(', '))
      setLoadingUpdate(false);
      return;
    }
    const response = await clientService.Edit(updatedClient);

    if (response.success !== true) {
      Toast.Show("error", response.message);
      setLoadingUpdate(false);
      return;
    }
    Toast.Show("success", response.message);
    setLoadingUpdate(false);
  };

  const deleteClient = async () => {
    setLoadingUpdate(true);
    const response = await clientService.Delete(Number(id));
    setLoadingUpdate(false);

    if (response.success !== true) {
      Toast.Show("error", response.message);
      return;
    }
    navigate(-1);
    Toast.Show("success", response.message);
  };

  //rentabilidade
  //obter valor setting (módulo rentabilidade)
  useEffect(() => {
    if (availableModules.includes("ProfitabilitySage50cIsActiveProfitabilityModule")) {
      setIsActiveProfitabilityModule(true);
    }
  }, [availableModules]);

  return (
    <Layout>
      <div className="pageContainer">
        <div className="pageHeader">
          <PageTitle title={"Editar Cliente"} allowGoBack={true} />
        </div>
        <div className="pageBody">

          <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-start", alignContent: "flex-start" }}>
            <Col xl={6}>
              <label style={{ margin: "13px 0", padding: "0px" }}>
                <b>Dados do cliente</b>
              </label>

              <Row>
                <InputComponent
                  xl={12}
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={client?.name}
                  label={intl.formatMessage({ id: "Screens.Client.Info.Name" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setClient({ ...client, name: t })
                  }}
                  required
                />
              </Row>
              <Row>
                <InputComponent
                  xl={3}
                  lg={3}
                  md={3}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={client?.vatNumber}
                  label={intl.formatMessage({ id: "Screens.Client.Info.NIF" })}
                  readOnly={true}
                  required
                />
                <InputComponent
                  xl={3}
                  lg={3}
                  md={3}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"number"}
                  value={client?.phoneNumber}
                  label={intl.formatMessage({ id: "Screens.Client.Info.PhoneNumber" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setClient({ ...client, phoneNumber: t })
                  }}
                  required
                />
                <InputComponent
                  xl={6}
                  lg={6}
                  md={6}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={client?.email}
                  label={intl.formatMessage({ id: "Screens.Client.Info.Email" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setClient({ ...client, email: t })
                  }}
                  required
                />
              </Row>
              <Row>
                <InputComponent
                  xl={6}
                  lg={6}
                  md={6}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={client?.address}
                  label={intl.formatMessage({ id: "Screens.Client.Info.Street" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setClient({ ...client, address: t })
                  }}
                />
                <InputComponent
                  xl={6}
                  lg={6}
                  md={6}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={client?.address2}
                  label={intl.formatMessage({ id: "Screens.Client.Info.Street2" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setClient({ ...client, address2: t })
                  }}
                />
              </Row>
              <Row>
                <InputComponent
                  xl={3}
                  lg={3}
                  md={3}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={client?.postalCode}
                  label={intl.formatMessage({ id: "Screens.Client.Info.PostalCode" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setClient({ ...client, postalCode: t })
                  }}
                />
                <InputComponent
                  xl={4}
                  lg={4}
                  md={4}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={client?.locality}
                  label={intl.formatMessage({ id: "Screens.Client.Info.City" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setClient({ ...client, locality: t })
                  }}
                />
                <SearchSelectComponent
                  xl={5}
                  lg={5}
                  md={5}
                  sm={12}
                  xs={12}
                  label={intl.formatMessage({ id: "Screens.Client.Info.NameCountry" })}
                  loadOptions={searchCountry}
                  onChange={(e: SelectItemsProps) => {
                    setCountrySelect(e);
                  }}
                  value={countrySelect}
                  style={{ width: "100%" }}
                  required
                />
              </Row>
              <Row>
                <InputComponent
                  xl={6}
                  lg={6}
                  md={6}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={client?.paymentConditions}
                  label={intl.formatMessage({ id: "Screens.Client.Info.PaymentType" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setClient({ ...client, paymentConditions: t })
                  }}
                />
                <InputComponent
                  xl={6}
                  lg={6}
                  md={6}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={client?.website}
                  label={intl.formatMessage({ id: "Screens.Client.Info.WebSite" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setClient({ ...client, website: t })
                  }}
                />
              </Row>
              <Row>
                <Col xl={3} lg={3} md={3} sm={12} xs={12} style={{ marginTop: "1em", display: "inline-flex", gap: "1em" }}>
                  <ButtonComponent
                    disabled={loadingUpdate}
                    loading={loadingUpdate}
                    text={intl.formatMessage({ id: "ClientEdit.Save" })}
                    onClick={editClient}
                  />

                  <ButtonComponent
                    disabled={loadingUpdate}
                    loading={loadingUpdate}
                    text={intl.formatMessage({ id: "ClientEdit.Remove" })}
                    onClick={deleteClient}
                  />
                </Col>
              </Row>
            </Col>

            <Col xl={6}>
              <ClientNavBar
                clientId={Number(id)}
                reloadExecutions={false}
                loadingUpdate={!loadingUpdate}
                client={client}
                clientErpId={client?.erpId}
                clientName={client?.name}
              />
            </Col>

          </Row>

        </div>
      </div>
    </Layout >
  );
}